<template>
  <!-- enter amount -->
  <div class="wrapper -mt-4" test-id="main">
    <!-- available balance -->
    <div>
      <!-- form -->
      <el-form
        :model="enteredAmountForm"
        ref="enteredAmountForm"
        :rules="rules"
        size="normal"
      >
        <!-- from balance flow -->
        <div
          class="mt-4 grid grid-cols-12 form-item bg-root-7 pl-3 input-animation"
          :class="{ 'input-error': isSendersInputError }"
        >
          <div class="flex flex-col col-span-8 md:col-span-8 px-5 pt-1 pb-1.5">
            <label
              class="font-normal text-gray-3 text-xs lg:text-sm mt-3 -mb-1.5 label"
              >Send {{ parseNetworkName(country.network) || '' }}</label
            >
            <div class="flex items-center pt-0 lg:pb-1 text-root-3">
              <!-- <span v-else class="text-root-3 text-xl md:text-3xl pb-5">{{
                country.symbol
              }}</span> -->
              <el-form-item prop="enteredAmount">
                <el-input
                  @input="handleInput('enteredAmount', calcFirstInput)"
                  class="text-base md:text-xl"
                  v-model="enteredAmountForm.enteredAmount"
                  name="amount"
                  placeholder="0.00"
                  :readonly="hasBeenDebited || isLoadingHub2ProviderBanks"
                  style="margin-left: -1rem"
                  id="sendMoneyTourItem1"
                >
                </el-input
              ></el-form-item>
            </div>
          </div>
          <div
            class="col-span-4 md:col-span-4 flex items-center justify-center drop-down"
          >
            <div
              class="font-light text-xs flex items-center justify-center gap-x-2 px-4 py-2 mr-2 lg:-mr-0.5 cursor-pointer rounded-lg transition-all duration-150 hover:shadow"
              v-on="
                !hasBeenDebited ? { click: () => openDialog('source') } : {}
              "
              id="sendMoneyTourItem2"
            >
              <currency-icon
                :currency="country.name"
                size="lg:h-7 lg:w-7 h-5 w-5"
              />
              <div class="capitalize lg:text-base text-sm">
                {{ country.currency }}
              </div>
              <caret-bottom-icon />
            </div>
          </div>
        </div>

        <transition name="slide-fade">
          <div>
            <div class="flex items-center justify-center relative lg:mb-4 mb-2">
              <div
                class="z-10 absolute transform translate-x-2/4 -left-12 right-0"
              >
                <switch-icon
                  @click.native="switchCurrency"
                  class="cursor-pointer mt-5"
                />
              </div>
            </div>
            <div
              class="grid grid-cols-12 form-item bg-root-7 pl-3 input-animation"
              :class="{ 'input-error': isRecipientInputError }"
            >
              <div class="flex flex-col col-span-8 px-5 pt-1 pb-1.5">
                <label
                  class="font-normal text-gray-3 text-xs lg:text-sm mt-3 -mb-1.5"
                  >Recipient will get
                </label>

                <div class="flex items-center pt-0 lg:pb-1">
                  <el-form-item prop="recipientAmount" class="text-root-3">
                    <el-input
                      v-if="transferTypeSelected === 'fromBalance'"
                      v-model="recipientAmountBalance"
                      :readonly="hasBeenDebited"
                      style="margin-left: -1rem"
                    ></el-input>
                    <el-input
                      v-else
                      @input="handleInput('recipientAmount', calcSecondInput)"
                      v-model="enteredAmountForm.recipientAmount"
                      placeholder="0.00"
                      :readonly="hasBeenDebited || isLoadingHub2ProviderBanks"
                      style="margin-left: -1rem"
                    ></el-input>
                  </el-form-item>
                </div>
              </div>
              <div
                class="col-span-4 flex items-center justify-center drop-down"
              >
                <div
                  class="font-light text-xs flex items-center justify-center gap-x-2 px-4 py-2 mr-2 lg:-mr-0.5 cursor-pointer rounded-lg transition-all duration-150 hover:shadow"
                  @click="openDialog('destination')"
                  id="sendMoneyTourItem3"
                >
                  <currency-icon
                    :currency="country2.name"
                    size="lg:h-7 lg:w-7 h-5 w-5"
                  />
                  <div class="capitalize text-sm lg:text-base">
                    {{ country2.currency }}
                  </div>
                  <caret-bottom-icon />
                </div>
              </div>
            </div>
            <div
              class="mt-5 px-5 lg:px-6 py-4 text-xs lg:text-sm text-root-4 font-normal"
            >
              <!-- payout method is currently only for ghs, xaf and xof -->
              <transition name="slide-fade">
                <div
                  v-if="mobileWalletSupportedCurrencies"
                  class="flex items-center justify-between w-full mb-5 border py-4 px-4 rounded-lg"
                >
                  <span>Payout Method</span>
                  <el-dropdown placement="bottom-end" class="cursor-pointer">
                    <span class="flex item-center justify-center">
                      <p>{{ payoutMethod.label }}</p>
                      <i class="el-icon-arrow-down el-icon--right mt-1" />
                    </span>
                    <el-dropdown-menu
                      class="rounded-xl w-72 lg:w-90 lg:-mr-4"
                      slot="dropdown"
                    >
                      <el-dropdown-item
                        v-for="(pMethods, index) in payoutMethods"
                        :key="index"
                        @click.native="changePayoutMethod(pMethods)"
                      >
                        <div
                          v-if="handleEnableMobileMoneyTransfer(pMethods)"
                          class="w-full py-2 flex items-center justify-between"
                        >
                          <span>{{ pMethods.label }}</span>
                          <i
                            v-if="payoutMethod.value === pMethods.value"
                            class="el-icon-check"
                          />
                        </div>
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </transition>
              <ul>
                <li
                  class="flex items-center justify-between mb-3 pb-3 border-b"
                >
                  <span>Exchange rate</span>
                  <span
                    class="text-root-11 text-primary font-bold px-2 py-1 rounded-3xl bg-info-subtle"
                    >1
                    {{
                      !this.isDivisionOperator
                        ? country.currency
                        : country2.currency
                    }}
                    = {{ exchangeRateBase }}
                    {{
                      !this.isDivisionOperator
                        ? country2.currency
                        : country.currency
                    }}</span
                  >
                </li>
                <li
                  class="flex items-center justify-between mb-3 pb-3 border-b"
                >
                  <span
                    >Our fees {{ percentageFees || ''
                    }}{{ percentageFees ? '%' : '' }}
                  </span>
                  <span class="text-root-2 font-medium">{{
                    parseMoneyAll(fees, country.currency)
                  }}</span>
                </li>
                <li
                  v-if="!fromHoldingsBalance && transferSourceIsLocal"
                  class="flex items-center justify-between mb-3 pb-3 border-b"
                  @click="editCommission"
                >
                  <span class="flex items-center gap-x-1 cursor-pointer"
                    ><span>Partner's commission </span>
                  </span>
                  <span
                    class="text-root-2 font-medium flex items-center gap-x-1"
                    @click="editCommission"
                    >{{ parseMoneyAll(partnerCommission, country.currency)
                    }}<edit-pen-icon
                  /></span>
                </li>
                <li class="flex items-center justify-between mb-3">
                  <span>Total amount we'll convert</span>
                  <span class="text-root-2 font-medium">{{
                    firstInputSelected
                      ? parseMoneyAll(
                          formatAmount(enteredAmountForm.enteredAmount) - fees,
                          country.currency
                        )
                      : parseMoneyAll(
                          formatAmount(enteredAmountForm.recipientAmount) /
                            exchangeRate,
                          country.currency
                        )
                  }}</span>
                </li>
              </ul>
            </div>
            <!-- continue button -->
            <div class="continue-button w-full mt-4" id="sendMoneyTourItem4">
              <el-tooltip
                content="You do not have the access to initiate a transfer"
                placement="top"
                :disabled="hasInitiatorAccess"
              >
                <el-button
                  :disabled="isButtonDisabled"
                  @click="validateForm('enteredAmountForm')"
                  class="bg-primary w-full text-white rounded-lg py-5 shadow-lg"
                >
                  Continue
                </el-button>
              </el-tooltip>
            </div>
          </div>
        </transition>
      </el-form>
    </div>
    <!-- commission setting dialog -->
    <edit-partner-commission-dialog
      v-if="showPartnerCommissionDialog"
      :showDialog="showPartnerCommissionDialog"
      @closeDialog="closeDialog('showPartnerCommissionDialog')"
    />
    <!-- transfer reason dialog -->
    <transfer-reason
      v-if="showTransferReasonDialog"
      :showDialog="showTransferReasonDialog"
      @closeDialog="closeDialog('showTransferReasonDialog')"
      @next="next"
    />
    <select-currency
      v-if="showSelectCurrencyDialog"
      :showDialog="showSelectCurrencyDialog"
      :currencyType="currencyType"
      :inputEmpty="!enteredAmountForm.enteredAmount"
      @closeDialog="closeDialog('showSelectCurrencyDialog')"
    />
    <pending-asset-transfer
      v-if="showPendingAssetTransferDialog"
      :showDialog="showPendingAssetTransferDialog"
      @showDebitedAmount="showDebitedAmountFlow"
      @closeDialog="closeDialog('showPendingAssetTransferDialog')"
    />
    <terminal-restricted v-if="!hasKyc" :showDialog="!hasKyc" />
  </div>
</template>

<script>
import {
  FormatAmount,
  AddComma,
  ParseMoneyAll,
  ParseMoney,
  ParseNetworkName,
  ParseAmount
} from '@/utils/helpers'
import {
  COUNTRIES,
  LOCAL_COUNTRIES,
  GHS_PAYOUT_METHODS,
  ENABLED_MULTI_CURRENCY_AND_METHODS
} from '@/utils/constants'
import localDeposit from '@/mixins/localDeposit.js'
import getBalance from '@/mixins/getBalance.js'
import country from '@/mixins/getCountryDetails.js'
import getPendingDeposits from '@/mixins/pendingDeposit.js'
import hasKyc from '@/mixins/app.js'
import switchCurrency from '@/mixins/terminal.js'
import tenant from '@/mixins/tenant.js'
import userKinds from '@/mixins/userKinds.js'
import provider from '@/mixins/provider.js'

export default {
  mixins: [
    localDeposit,
    country,
    getBalance,
    getPendingDeposits,
    hasKyc,
    switchCurrency,
    tenant,
    userKinds,
    provider
  ],
  components: {
    EditPenIcon: () => import('@/assets/icons/EditPenIcon.vue'),
    CaretBottomIcon: () => import('@/assets/v2/icons/CaretBottomIcon'),
    SwitchIcon: () => import('@/assets/v2/icons/SwitchIcon.vue'),
    TransferReason: () =>
      import('@/components/v2/terminal/dialogs/TransferReason.vue'),
    PendingAssetTransfer: () =>
      import('@/components/terminal/dialogs/PendingAssetTransfer.vue'),
    CurrencyIcon: () => import('@/assets/icons/CurrencyIcons.vue'),
    EditPartnerCommissionDialog: () =>
      import('@/components/terminal/EditPartnerCommissionDialog.vue'),
    SelectCurrency: () =>
      import('@/components/terminal/dialogs/SelectCurrency.vue'),
    TerminalRestricted: () =>
      import('@/components/terminal/dialogs/TerminalRestricted.vue')
  },
  data () {
    return {
      currencyOptions: COUNTRIES,
      localCurrencyOptions: LOCAL_COUNTRIES,
      payoutMethods: GHS_PAYOUT_METHODS,
      showPartnerCommissionDialog: false,
      showSelectCurrencyDialog: false,
      showTransferReasonDialog: false,
      showPendingAssetTransferDialog: false,
      showPendingAssetTransfer: false,
      firstInputSelected: true,
      switchRecipientAmount: 0,
      showRecipientField: false,
      isLoadingHub2ProviderBanks: false,
      payoutMethod: {},
      currencyType: '',
      enteredAmountForm: {
        enteredAmount: '',
        recipientAmount: ''
      },
      rules: {
        enteredAmount: [
          { validator: this.validateAmount, trigger: ['blur', 'change'] }
        ],
        recipientAmount: [
          {
            validator: this.validateRecipientAmount,
            trigger: ['blur', 'change']
          }
        ]
      },
      isRecipientInputError: false,
      isSendersInputError: false
    }
  },
  computed: {
    providerConfig () {
      return provider
    },
    showPendingAsset () {
      return this.enteredAmountForm.enteredAmount || this.showRecipientField
    },
    computeShowPendingAssetTransfer () {
      return this.showPendingAssetTransfer
    },
    isButtonDisabled () {
      return (
        !this.enteredAmountForm.enteredAmount ||
        (this.fromHoldingsBalance &&
          this.formatAmount(this.enteredAmountForm.enteredAmount) >
            this.selectedHoldingsAccountBalance) ||
        (!this.fromHoldingsBalance && !this.recipientAmount) ||
        !this.hasInitiatorAccess
      )
    },
    walletAddresses () {
      return this.$store.state.transactions.walletAddresses.items || []
    },
    transferable () {
      const transferDetails = this.$store.state.auth.transferDetails
      const details = transferDetails.find(
        (detail) =>
          detail.from === this.secondaryCurrency &&
          detail.to === this.activeCurrency
      )
      const walletDetails = this.walletAddresses.find(
        (wallet) =>
          wallet.asset === this.secondaryCurrency &&
          wallet.reason === 'transfer'
      )
      if (this.transferDestinationIsAsset && !walletDetails) {
        return false
      }
      return details?.is_enabled
    },
    hasBeenDebited () {
      return this.$store.state.terminal.hasBeenDebited
    },
    partnerCommission () {
      return (
        parseInt(
          this.$store.state.terminal.transferTerminalDetails?.transferDetails
            ?.partnerCommission
        ) || 0
      )
    },
    fees () {
      if (this.enteredAmountForm.enteredAmount) {
        if (this.percentageFees) {
          return (
            (this.percentageFees / 100) *
            FormatAmount(this.enteredAmountForm.enteredAmount)
          ).toFixed(2)
        } else if (this.flatFees) {
          return this.flatFees
        } else {
          return 0
        }
      } else {
        return 0
      }
    },
    recipientAmount: function () {
      const charges = this.fees
      const exchangedAmount =
        (FormatAmount(this.enteredAmountForm.enteredAmount) - charges) *
        this.exchangeRate
      if (!exchangedAmount || exchangedAmount <= 0) {
        return 0
      }
      return AddComma(parseFloat(exchangedAmount).toFixed(2).toString())
    },
    enteredAmount: function () {
      const amountToConvert =
        FormatAmount(this.enteredAmountForm.recipientAmount) / this.exchangeRate
      const exchangedAmount = this.percentageFees
        ? amountToConvert / (1 - this.percentageFees / 100)
        : amountToConvert + this.flatFees
      if (!exchangedAmount || exchangedAmount <= 0) {
        return AddComma(parseFloat(0).toString())
      }
      return AddComma(parseFloat(exchangedAmount).toFixed(2).toString())
    },
    recipientAmountBalance: function () {
      const exchangedAmount =
        FormatAmount(this.enteredAmountForm.enteredAmount) - this.fees
      return parseFloat(exchangedAmount)
    },
    transferTypeSelected () {
      return this.$store.state.terminal.terminalTransferType
    },
    transferDetails () {
      return (
        this.$store.state.terminal.transferTerminalDetails.transferDetails || {}
      )
    },
    fromBalance () {
      return this.$store.state.terminal.terminalTransferType === 'fromBalance'
    },
    isEnableMobileMoneyTransfer () {
      return process.env.VUE_APP_ENABLE_MOBILE_MONEY_TRANSFER === 'true'
    }
  },
  methods: {
    clearAmountInput () {
      this.enteredAmountForm.enteredAmount = ''
      this.enteredAmountForm.recipientAmount = ''
    },
    handleEnableMobileMoneyTransfer (payoutMethod) {
      if (payoutMethod.value === this.$transactionType.MOBILE_MONEY) {
        const isEnabledInConfig = ENABLED_MULTI_CURRENCY_AND_METHODS.find(
          (currency) => currency.currency === this.secondaryCurrency
        ).isMobileMoneyEnabled
        return this.isEnableMobileMoneyTransfer && isEnabledInConfig
      }
      if (payoutMethod.value === this.$transactionType.BANK_TRANSFER) {
        const isEnabledInConfig = ENABLED_MULTI_CURRENCY_AND_METHODS.find(
          (currency) => currency.currency === this.secondaryCurrency
        ).isBankTransferEnabled
        return isEnabledInConfig
      }
      return true
    },
    changePayoutMethod (payoutMethod) {
      this.payoutMethod = payoutMethod
      // so recipient information doesn't persist
      this.resetRecipientInformation()
    },
    resetRecipientInformation () {
      this.$store.state.terminal.transferTerminalDetails.recipientInformation =
        {
          ...this.$store.state.terminal.transferTerminalDetails
            .recipientInformation,
          accountName: '',
          accountNo: '',
          bankAddress: '',
          bankBranch: '',
          bankCity: '',
          bankCode: '',
          bankName: '',
          providerCode: ''
        }
    },
    showDebitedAmountFlow () {
      const debit = this.transferDetails.debitAmount
      this.enteredAmountForm.enteredAmount = debit
      this.openDialog('destination')
    },
    goToMakePayment () {
      this.$emit('showMakePayment')
    },
    parseNetworkName (networkType) {
      return ParseNetworkName(networkType)
    },
    changeSecondaryCountry () {
      if (this.inputAmountFieldIsNotEmpty) {
        this.calcSecondInput()
      }
      this.$store.dispatch('common/setSecondaryCountry', this.secondaryCurrency)
    },
    changeActiveCountry () {
      if (this.inputAmountFieldIsNotEmpty) {
        this.calcFirstInput()
      }
      if (this.activeCurrency !== this.$store.state.common.activeCountry) {
        this.$store.dispatch('common/setActiveCountry', this.activeCurrency)
      }
      this.$store.dispatch(
        'common/setSecondaryCountry',
        this.$store.state.common.secondaryCountry === 'usa'
          ? 'nigeria'
          : this.secondaryCurrency
      )
    },
    editCommission () {
      this.showPartnerCommissionDialog = true
    },
    formatAmount (val) {
      if (val) {
        return FormatAmount(val)
      }
      return 0
    },
    validateAmount (rule, value, callback) {
      this.isSendersInputError = true
      const totalDebitAmount = FormatAmount(value)
      if (totalDebitAmount < 10) {
        callback(new Error(`Minimum amount is ${this.country.symbol}10`))
      } else if (totalDebitAmount > 9999999999) {
        callback(
          new Error(
            `Maximum amount is ${this.country.symbol} ${AddComma('9999999999')}`
          )
        )
      } else if (
        this.fromHoldingsBalance &&
        totalDebitAmount > this.selectedHoldingsAccountBalance
      ) {
        callback(new Error("That's more than your balance"))
      } else if (!totalDebitAmount) {
        callback(new Error('Provide an amount'))
      } else {
        this.isSendersInputError = false
        callback()
      }
    },
    validateRecipientAmount (rule, value, callback) {
      this.isRecipientInputError = true
      const toReceiveAmount = FormatAmount(value)
      if (toReceiveAmount < 10) {
        callback(new Error(`Minimum amount is ${this.country2.symbol}10`))
      } else if (toReceiveAmount > 9999999999) {
        callback(
          new Error(
            `Maximum amount is ${this.country2.symbol} ${AddComma(
              '9999999999'
            )}`
          )
        )
      } else if (toReceiveAmount > this.totalBalance && this.fromBalance) {
        this.isRecipientInputError = true
        callback(new Error("That's more than your balance"))
      } else if (!toReceiveAmount) {
        callback(new Error('Provide an amount'))
      } else {
        this.isRecipientInputError = false
        callback()
      }
    },
    parseMoneyAll (val, currency) {
      return ParseMoneyAll(val, currency)
    },
    parseMoney (val) {
      ParseMoney(val)
    },
    next (transferReason) {
      const details = {
        transferReason: transferReason,
        debitAmount: FormatAmount(this.enteredAmountForm.enteredAmount),
        fees: this.fees,
        recipientAmount: this.fromBalance
          ? this.recipientAmountBalance
          : AddComma(
              parseFloat(FormatAmount(this.recipientAmount))
                .toFixed(2)
                .toString()
            ),
        partnerCommission:
          !this.transferSourceIsGlobalFiat && !this.transferSourceIsAsset
            ? this.partnerCommission
            : 0
      }
      // if secondary currency is ghs
      if (this.mobileWalletSupportedCurrencies) {
        details.payoutMethod = this.payoutMethod.value
      }
      this.$store.dispatch('terminal/setTransferDetails', details)
      this.$emit('next')
    },
    validateForm (enteredAmountForm) {
      this.$refs[enteredAmountForm].validate((valid) => {
        if (valid) {
          this.continueHandler()
        } else {
          this.$notify.error({
            title: 'Invalid input',
            message: 'Provide a valid amount',
            position: 'top-right'
          })
        }
      })
    },
    continueHandler () {
      this.showTransferReasonDialog = true
    },
    openDialog (type) {
      if (type === 'source') {
        this.showRecipientField = true
      }
      this.currencyType = type
      this.showSelectCurrencyDialog = true
    },
    closeDialog (dialog) {
      this[dialog] = false
      if (dialog === 'showPartnerCommissionDialog') return
      if (
        this.enteredAmountForm.enteredAmount <= 0 ||
        this.enteredAmountForm.enteredAmount === ''
      ) {
      } else {
        this.firstInputSelected = this.currencyType === 'destination'
        this.calculate()
      }
    },
    pendingAssetDeposits () {
      const payload = {
        page: 1,
        size: 10,
        status: 'pending', // should be updated to pending later
        type: 'asset_deposit',
        reason: 'transfer',
        from: '',
        to: ''
      }
      this.getDeposits(payload)
    },
    setPayoutMethod () {
      // check if there is already a value in the store
      const paymentMethod = this.payoutMethods.find(
        (method) =>
          method.value ===
          this.$store.state.terminal.transferTerminalDetails.transferDetails
            .payoutMethod
      )
      // get default payout method for XAF
      const defaultPayoutMethod = this.payoutMethods.find(
        (item) => item.value === this.$transactionType.MOBILE_MONEY
      )
      paymentMethod && !this.secondaryCurrencyIsXAF
        ? (this.payoutMethod = paymentMethod)
        : (this.payoutMethod = defaultPayoutMethod)
      // so recipient information doesn't persist
      this.resetRecipientInformation()
    }
  },
  async beforeMount () {
    if (this.hasKyc) {
      await this.pendingAssetDeposits()
      if (this.exchangeRate === 0) {
        const message = 'Cannot get current exchange rate at the moment'
        this.$message({
          showClose: true,
          message: `${message}, please try again Later`,
          type: 'error',
          duration: 5000
        })
        this.$store.dispatch('common/setError', message)
        setTimeout(() => {
          this.$router.push('system-error')
        }, 3000)
      }
      if (
        this.transferDetails &&
        Object.keys(this.transferDetails).length > 0
      ) {
        this.enteredAmountForm.enteredAmount = this.transferDetails.debitAmount
        this.enteredAmountForm.recipientAmount =
          this.transferDetails.recipientAmount
      }
      if (this.hasBeenDebited) {
        this.openDialog('destination')
      }
      this.getBeneficiaries()
    }
    // if secondary currency is ghs, xof or xaf
    if (this.mobileWalletSupportedCurrencies) this.setPayoutMethod()
  },
  watch: {
    'enteredAmountForm.enteredAmount': function (newValue) {
      const result = ParseAmount(newValue)
      this.$nextTick(() => {
        this.enteredAmountForm.enteredAmount = result
      })
    },
    '$store.state.terminal.depositMethod': function (newValue) {
      this.$nextTick(() => {
        this.calculate()
      })
    },
    'enteredAmountForm.recipientAmount': function (newValue) {
      const result = ParseAmount(newValue)
      this.$nextTick(() => {
        this.enteredAmountForm.recipientAmount = result
      })
    },
    '$store.state.transactions.deposits.items': function () {
      if (
        this.$store.state.transactions.deposits.items.length > 0 &&
        !this.hasBeenDebited
      ) {
        this.showPendingAssetTransferDialog = true
      }
    },
    '$store.state.common.secondaryCountry': function () {
      this.setPayoutMethod()
      // for hub2
      this.handleHub2ProviderPayout(
        {
          payoutMethod: this.payoutMethod.value,
          currency: this.country2.currency
        },
        this.clearAmountInput
      )
    },
    deep: true
  }
}
</script>

<style scoped>
>>> .el-select {
  width: 110px;
  margin-right: -30px;
}

>>> .el-select .el-input .el-select__caret {
  color: var(--color-primary);
  font-size: 13px;
  font-weight: 800;
  transition: transform 0.3s;
  transform: rotateZ(180deg);
  cursor: pointer;
}
>>> .el-dropdown-menu__item:not(.is-disabled):hover,
.el-dropdown-menu__item:focus {
  background-color: #ecf5ff;
  color: var(--color-primary);
}
>>> .form-item {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 1px 3px rgba(3, 2, 7, 0.12);
  border-radius: 10px;
}

.form-item-2 {
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.12),
    0px 1px 3px rgba(3, 2, 7, 0.12);
  border-radius: 15px;
}

.drop-down {
  border-radius: 0px 15px 15px 0px;
}

>>> .el-popper[x-placement^='bottom'] {
  margin-left: -100px;
}

>>> .el-input__inner {
  border: none;
  outline: none;
  padding-right: 0rem;
  background: transparent;
  font-size: 1.6rem;
  font-weight: 700;
}

.form-item-1 ul {
  list-style: none;
  border-left: 1px solid var(--text-3);
}

.form-item-1 li {
  position: relative;
}

.form-item-1 li::before {
  content: '•';
  font-size: 1.8rem;
  position: absolute;
  left: -0.4rem;
  top: 0;
  color: var(--color-primary);
}

.form-item-1 li:last-of-type::before {
  left: -0.5rem;
}

.slide-fade-enter-active {
  animation: slide-fade 0.3s ease forwards;
}

.slide-fade-leave-active {
  animation: slide-fade 0.3s ease forwards alternate-reverse;
}

/* animation for  */
@keyframes slide-fade {
  0% {
    opacity: 0;
    transform: translateY(-20%);
  }

  70% {
    opacity: 0.7;
    transform: translateY(10%);
  }

  100% {
    opacity: 1;
    transform: translateY(0%);
  }
}

@media screen and (max-width: 767px) {
  >>> .el-input__inner {
    font-size: 1.5rem;
  }

  >>> .el-select {
    margin-right: -10px;
    width: 80px;
  }

  >>> .el-select .el-input .el-select__caret {
    margin-right: 0.15rem;
  }

  >>> .el-select .el-input__inner {
    font-size: 1rem;
  }
}
</style>
